<template>
  <div
    class="exercise-tile"
    :data-id="calendarId"
    :data-type="type"
    :data-type-id="id"
    :class="copied ? 'copied' : ''"
  >
    <div
      @mouseover="$emit('mouseover')"
      @mouseleave="$emit('mouseleave')"
      v-if="type == 'EXERCISE' || type == 'ACTIVITY' || noExerciseThumbnails"
      class="exercise-tile__thumbnail--wrap"
      :class="
        (additionalMedia && additionalMedia.processing) ||
        (!additionalMedia && media && media.processing)
          ? 'processing'
          : ''
      "
    >
      <template
        v-if="
          additionalMedia &&
            (additionalMedia.thumbnailUrl || additionalMedia.processing)
        "
      >
        <img
          v-if="
            additionalMedia &&
              additionalMedia.thumbnailUrl &&
              !additionalMedia.processing
          "
          class="exercise-tile__thumbnail"
          :src="additionalMedia.thumbnailUrl"
        />
        <v-progress-circular
          v-if="
            additionalMedia &&
              additionalMedia.processing &&
              !additionalMedia.thumbnailUrl
          "
          class="exercise-tile__thumbnail--loader"
          size="32"
          indeterminate
          color="bhBlue3"
          :width="3"
        ></v-progress-circular>
      </template>
      <template v-else-if="media && (media.thumbnailUrl || media.processing)">
        <img
          v-if="media && media.thumbnailUrl && !media.processing"
          class="exercise-tile__thumbnail"
          :src="media.thumbnailUrl"
        />
        <v-progress-circular
          v-if="media && media.processing && !media.thumbnailUrl"
          class="exercise-tile__thumbnail--loader"
          size="32"
          indeterminate
          color="bhBlue3"
          :width="3"
        ></v-progress-circular>
      </template>
      <img
        v-else-if="mobileStaticMedia && mobileStaticMedia.mediaUrl"
        class="exercise-tile__thumbnail"
        :src="mobileStaticMedia.mediaUrl"
      />
      <img
        v-else
        class="exercise-tile__thumbnail"
        :src="placeholder(media.fileTypeId)"
      />
      <v-progress-linear
        v-if="hoverLoader"
        background-color="rgba(255,255,255,0.3)"
        class="exercise-tile__thumbnail--preview"
        :value="hoverLoader"
      ></v-progress-linear>
      <div
        class="exercise-tile__thumbnail--icon"
        :style="{ background: typeIcon.color }"
      >
        <v-icon size="16">{{ typeIcon.icon }}</v-icon>
        <span v-if="time">{{ time == 'EVENING' ? 'PM' : 'AM' }}</span>
      </div>
    </div>
    <div class="exercise-tile__thumbnail--wrap" v-else>
      <v-progress-linear
        v-if="hoverLoader"
        background-color="rgba(255,255,255,0.3)"
        class="exercise-tile__thumbnail--preview"
        :value="hoverLoader"
      ></v-progress-linear>
      <multi-thumbnail :media="multiThumbnailComputed" :size="64" />
      <div
        class="exercise-tile__thumbnail--icon"
        :style="{ background: typeIcon.color }"
      >
        <v-icon size="16">{{ typeIcon.icon }}</v-icon>
      </div>
    </div>
    <div v-if="type == 'EXERCISE'" class="exercise-tile__text">
      <h5>{{ title }}</h5>
      <v-tooltip v-if="description" bottom open-delay="200">
        <template v-slot:activator="{ on, attrs }">
          <p v-bind="attrs" v-on="on">{{ description }}</p>
        </template>
        <p>{{ description }}</p>
      </v-tooltip>
    </div>
    <div v-else-if="type == 'ACTIVITY'" class="exercise-tile__text">
      <h6 v-if="ownership == 'SELF'">SELF-ADDED</h6>
      <h5>{{ title }}</h5>
      <v-tooltip
        v-if="description"
        bottom
        open-delay="200"
        @mouseover.native.stop
      >
        <template v-slot:activator="{ on, attrs }" @mouseover.native.stop>
          <p v-bind="attrs" v-on="on">{{ description }}</p>
        </template>
        <span>{{ description }}</span>
      </v-tooltip>
    </div>
    <div v-else class="exercise-tile__text">
      <h6>Circuit</h6>
      <h5>{{ title }}</h5>
      <p v-if="multiThumbnail && multiThumbnail.length > 0">
        {{ multiThumbnail.length }} Single exercise{{
          multiThumbnail.length > 1 ? 's' : ''
        }}
      </p>
    </div>
    <exercise-more-menu
      v-if="!hideMenu"
      class="exercise-tile__more"
      :id="id"
      :type="type"
      :menu="menuHandler"
      @changeMenuType="changeMenu"
      v-on="$listeners"
      :editable="editable"
      :movable="movable"
      :readonly="readonly"
      :duplicable="duplicable"
      :copyable="copyable"
      :sharable="sharable"
      :sharable-to-administration="sharableToAdministration"
      @import="importExercise(id, type)"
      @preview="previewExercise(id, type)"
    ></exercise-more-menu>
    <v-btn
      icon
      small
      text
      v-if="readonly && collectionMode"
      class="exercise-tile__collection"
      :class="collectionIsOwned ? 'owned' : ''"
    >
      <v-icon>bhi-copy</v-icon>
    </v-btn>
    <v-btn
      small
      text
      v-if="
        allowHideDetails && (!series || (series && series.exercises.length > 0))
      "
      @click="toggleHideDetails"
    >
      <v-icon class="mr-1">{{
        hiddenDetails ? 'bhi-chevron-down' : 'bhi-chevron-up'
      }}</v-icon
      >Details
    </v-btn>
    <div
      class="exercise-tile__quick-edit"
      v-if="
        exercise && (!allowHideDetails || (allowHideDetails && !hiddenDetails))
      "
    >
      <div
        class="exercise-tile__quick-edit--input"
        v-for="key in exerciseAttributes"
        :key="key.id"
      >
        <div>
          <v-text-field
            dense
            solo
            flat
            hide-details
            :value="exercise[key.id]"
            :readonly="!quickEditable"
            :disabled="!quickEditable"
            @keypress="filterQuickEdit"
            @mousedown.stop
            @click.stop="filterQuickEditPressed"
            @input="onInput(key.id, $event)"
          />
          <v-tooltip bottom v-if="key.tooltip">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ key.label }}</span>
            </template>
            <span>{{ key.tooltip }}</span>
          </v-tooltip>
          <span v-else>{{ key.label }}</span>
        </div>
      </div>
    </div>
    <div
      class="exercise-tile__quick-edit series"
      v-if="
        series && (!allowHideDetails || (allowHideDetails && !hiddenDetails))
      "
    >
      <div
        class="exercise-tile__quick-edit--item"
        v-for="item in series.exercises || series.exerciseDefinitions"
        :key="item.id"
      >
        <h5>{{ item.title }}</h5>
        <div
          class="exercise-tile__quick-edit--input"
          v-for="key in exerciseAttributes"
          :key="key.id"
        >
          <div>
            <v-text-field
              dense
              solo
              flat
              hide-details
              :value="item[key.id]"
              :readonly="!quickEditable"
              :disabled="!quickEditable"
              @keypress="filterQuickEdit"
              @mousedown.stop
              @click.stop="filterQuickEditPressed"
              @input="onSeriesInput(item.id, key.id, $event)"
            />
            <v-tooltip bottom v-if="key.tooltip">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">{{ key.label }}</span>
              </template>
              <span>{{ key.tooltip }}</span>
            </v-tooltip>
            <span v-else>{{ key.label }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="exercise-tile__quick-edit activity"
      v-if="
        activity && (!allowHideDetails || (allowHideDetails && !hiddenDetails))
      "
    >
      <v-btn-toggle
        v-model="activityTime"
        color="accent"
        rounded
        borderless
        @change="onInput('time', $event)"
      >
        <v-btn value="MORNING" x-small :disabled="!quickEditable">
          AM
        </v-btn>
        <v-btn value="EVENING" x-small :disabled="!quickEditable">
          PM
        </v-btn>
      </v-btn-toggle>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import MultiThumbnail from '@/components/avatars/MultiThumbnail'

import ExerciseMoreMenu from '@/components/exercises/ExerciseMoreMenu.vue'
export default {
  name: 'ExerciseTile',
  components: {
    ExerciseMoreMenu,
    MultiThumbnail,
  },
  props: {
    id: {
      type: Number,
      default: 0,
    },
    calendarId: {
      type: [Number, String],
      default: 0,
    },
    type: {
      type: String,
      default: 'EXERCISE',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    additionalMedia: {
      type: [Object, null],
      default: () => ({
        fileTypeId: 18,
        thumbnailUrl: null,
        previewUrl: null,
        processing: false,
      }),
    },
    media: {
      type: [Object, null],
      default: () => ({
        fileTypeId: 18,
        thumbnailUrl: null,
        previewUrl: null,
        processing: false,
      }),
    },
    mobileStaticMedia: {
      type: [Object, null],
      default: null,
    },
    multiThumbnail: {
      type: [Array, null],
      default: null,
    },
    getResourceById: {
      type: Function,
      default: () => null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    collectionMode: {
      type: Boolean,
      default: false,
    },
    collectionIsOwned: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    quickEditable: {
      type: Boolean,
      default: true,
    },
    movable: {
      type: Boolean,
      default: false,
    },
    hoverLoader: {
      type: Number,
      default: 0,
    },
    hideMenu: {
      type: Boolean,
      default: false,
    },
    duplicable: {
      type: Boolean,
      default: false,
    },
    copyable: {
      type: Boolean,
      default: false,
    },
    copied: {
      type: Boolean,
      default: false,
    },
    sharable: {
      type: Boolean,
      default: false,
    },
    sharableToAdministration: {
      type: Boolean,
      default: false,
    },
    time: {
      type: [Boolean, String],
      default: false,
    },
    ownership: {
      type: [Boolean, String],
      default: false,
    },
    exercise: {
      type: [Boolean, Object],
      default: false,
    },
    series: {
      type: [Boolean, Object],
      default: false,
    },
    activity: {
      type: [Boolean, Object],
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    allowHideDetails: {
      type: Boolean,
      default: false,
    },
    defaultHideDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activityTime: 'none',
      menuHandler: false,
      exerciseResource: null,
      exerciseAttributes: [
        { id: 'repetitions', label: 'Reps' },
        { id: 'series', label: 'Sets' },
        { id: 'weight', label: 'lbs' },
        { id: 'time', label: 'minutes' },
        { id: 'distance', label: 'Miles' },
        {
          id: 'restTimeBetweenSeries',
          label: 'RTBS',
          tooltip: 'Rest time between sets',
        },
      ],
      hiddenDetails: false,
    }
  },
  watch: {
    activity: {
      handler: function(val) {
        if (val?.time) {
          this.activityTime = val.time
        }
      },
      deep: true,
    },
  },
  created() {
    if (this.defaultHideDetails === true || this.defaultHideDetails === false)
      this.hiddenDetails = this.defaultHideDetails
    if (this.activity?.time) this.activityTime = this.activity.time
  },
  computed: {
    ...mapState('dictionariesStates', ['dictionaryFileType']),
    multiThumbnailComputed() {
      return this.multiThumbnail?.map(item => {
        if (item.media) item.thumbnailUrl = item.media.thumbnailUrl
        if (!item.media?.thumbnailUrl && item.additionalMedia)
          item.thumbnailUrl = item.additionalMedia.thumbnailUrl
        if (!item.thumbnailUrl && item.mobileStaticMedia?.mediaUrl)
          item.thumbnailUrl = item.mobileStaticMedia.mediaUrl
        return item
      })
    },
    typeIcon() {
      let typeObj = {
        color: '',
        icon: '',
      }
      switch (this.type) {
        case 'SERIES':
          typeObj.color = '#FAC032'
          typeObj.icon = 'bhi-list'
          break
        case 'ACTIVITY':
          typeObj.color = '#ED8938'
          typeObj.icon = 'bhi-activity'
          break
        default:
          typeObj.color = '#945EFF'
          typeObj.icon = 'bhi-exercise'
          break
      }
      return typeObj
    },
    noExerciseThumbnails() {
      if (
        this.type == 'SERIES' &&
        this.multiThumbnailComputed.findIndex(item => item.thumbnailUrl) == -1
      )
        return true
      else return false
    },
  },
  methods: {
    toggleHideDetails() {
      this.hiddenDetails = !this.hiddenDetails
    },
    changeMenu(type) {
      this.menuHandler = type
    },
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'
      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
    importExercise(id, type) {
      this.$emit('import-exercise', id, type)
    },
    previewExercise(id, type) {
      this.$emit('preview-exercise', id, type)
    },
    onInput(key, value) {
      if (!this.quickEditable) return
      if (value?.slice(-1) == '.') value = value.slice(0, -1)
      this.$emit('quick-edit', {
        exerciseId: this.calendarId,
        type: this.type,
        key: key,
        value: value,
      })
    },
    onSeriesInput(id, key, value) {
      if (!this.quickEditable) return
      if (value?.slice(-1) == '.') value = value.slice(0, -1)
      if (this.series.exercises) {
        let exercise = this.series.exercises.find(item => item.id == id)
        if (exercise)
          this.$emit('series-quick-edit', {
            exercise: exercise,
            exerciseId: exercise.id,
            type: exercise.type,
            key: key,
            value: value,
          })
      } else {
        let exercise = this.series.exerciseDefinitions.find(
          item => item.id == id,
        )
        if (exercise)
          this.$emit('series-quick-edit', {
            exercise: exercise,
            exerciseId: exercise.id,
            type: exercise.type,
            key: key,
            value: value,
          })
      }
    },
    filterQuickEdit(event) {
      if (!this.quickEditable) return
      event = event ? event : window.event
      let expect = event.target.value.toString() + event.key.toString()
      if (!/^[-+]?[0-9]*\.?[0-9]*$/.test(expect)) {
        event.preventDefault()
      } else {
        return true
      }
    },
    filterQuickEditPressed(event) {
      if (this.quickEditable) {
        event = event ? event : window.event
        event.preventDefault()
        event.stopPropagetion()
        return null
      }
    },
  },
}
</script>

<style lang="scss">
.exercise-tile {
  width: 280px;
  min-height: 78px;
  border-radius: 8px;
  border: 3px solid $bh-white;
  padding: 4px;
  padding-left: 68px;
  background: $bh-white;
  -webkit-transition-duration: 150ms;
  -moz-transition-duration: 150ms;
  -ms-transition-duration: 150ms;
  -o-transition-duration: 150ms;
  transition-duration: 150ms;
  display: flex;
  align-items: flex-start;
  margin: $gutter/2;
  flex-wrap: nowrap;
  position: relative;
  user-select: none;
  flex-wrap: wrap;
  align-content: flex-start;
  &.selected {
    border-color: $bh-blue--2;
    cursor: grab;
  }
  &.copied {
    border-color: $bh-blue--2 !important;
  }
  &:hover {
    .exercise-tile__more {
      opacity: 1;
    }
    .exercise-tile__collection {
      opacity: 0;
    }
  }
  &.bordered {
    background: transparent;
    border-color: $bh-blue;
  }
  &.create {
    padding-left: $gutter;
    .v-btn {
      &:nth-child(n + 2) {
        margin-left: 10px;
      }
    }
  }
  &__thumbnail {
    width: 64px;
    height: 64px;
    object-fit: cover;
    display: block;
    &--wrap {
      overflow: hidden;
      position: absolute;
      top: 4px;
      left: 4px;
      width: 64px;
      height: 64px;
      flex-shrink: 0;
      text-align: center;
      border-radius: 8px;
      &.processing {
        background: $bh-gray--6;
      }
    }
    &--loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0px);
    }
    &--preview {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 1;
      transition: 0.2s linear !important;
      .v-progress-linear__background,
      .v-progress-linear__buffer,
      .v-progress-linear__determinate {
        transition: 0.2s linear !important;
      }
    }
    &--icon {
      position: absolute;
      bottom: 2px;
      left: 2px;
      min-width: 20px;
      height: 20px;
      border-radius: 6px;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 3;
      .v-icon {
        color: $bh-white;
        font-size: 12px;
      }
      span {
        font-size: 0.6rem;
        padding-right: 2px;
        color: $bh-white;
        font-weight: bold;
        padding-top: 2px;
      }
    }
  }
  &__text {
    flex-grow: 1;
    flex-shrink: 1;
    padding: 0 8px;
    position: relative;
    max-width: 100%;
    h5 {
      color: $bh-black;
      max-height: 2.5em;
      overflow: hidden;
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: calc(100% - 16px);
    }
    h6 {
      color: $bh-gray--2;
    }
    p {
      margin: 0;
      font-size: 0.8rem;
      max-width: 90%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__more {
    flex-shrink: 0;
    width: 32px;
    opacity: 0;
    z-index: 1;
    position: relative;
    transition-duration: 300ms;
    position: absolute;
    top: 20px;
    right: 4px;
  }
  &__collection {
    position: absolute;
    right: $gutter/2;
    top: $gutter/2;
    transition-duration: 300ms;
    &.owned {
      background: $bh-green;
      .v-icon {
        color: $bh-white;
      }
    }
  }
  &__quick-edit {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding-top: 4px;
    padding-left: 8px;
    &.series {
      flex-direction: column;
    }
    &.activity {
      margin-top: 8px;
    }
    &--item {
      display: flex;
      flex-wrap: wrap;
      h5 {
        width: 100%;
      }
    }
    &--input {
      width: 36px;
      text-align: center;
      margin-right: 4px;
      &:empty {
        display: none;
      }
      .v-input__slot {
        background: $bh-blue--4 !important;
        padding: 0 8px !important;
        font-size: 0.8rem;
      }
      .v-input__control {
        min-height: 26px !important;
      }
      span {
        font-size: 0.6rem;
      }
    }
  }
}
</style>
